import { MonoTypeOperatorFunction, Observable, of, throwError } from 'rxjs';
import { delay, retry } from 'rxjs/operators';

export interface GenericRetryStrategyOptions {
	maxRetryAttempts: number;
	scalingDuration: number;
	includedStatusCodes: number[];
	backoff?: 'linear' | 'exponential';
	jitter?: boolean;
	maxDelayMs?: number;
}

function calculateRetryDelay(
	retries: number,
	scalingDuration: number,
	backoff: 'linear' | 'exponential' | undefined,
	maxDelayMs: number | undefined,
	jitter: boolean | undefined
): number {
	const effectiveBackoff = backoff ?? 'linear';
	const effectiveJitter = jitter ?? false;
	let delayMs: number;
	if (effectiveBackoff === 'exponential') {
		delayMs = scalingDuration * Math.pow(2, retries - 1);
	} else {
		delayMs = retries * scalingDuration;
	}
	if (effectiveJitter) {
		const jitterFactor = (Math.random() - 0.5) * 0.5;
		delayMs = Math.max(0, Math.round(delayMs * (1 + jitterFactor)));
	}
	delayMs = Math.min(delayMs, maxDelayMs ?? delayMs);
	return delayMs;
}

export const genericRetryStrategy = <T>(
	options?: Partial<GenericRetryStrategyOptions>
): MonoTypeOperatorFunction<T> => {
	const parsedOptions: GenericRetryStrategyOptions = {
		maxRetryAttempts: options?.maxRetryAttempts ?? 5,
		scalingDuration: options?.scalingDuration ?? 300,
		includedStatusCodes: options?.includedStatusCodes ?? [0],
		backoff: options?.backoff ?? 'linear',
		jitter: options?.jitter ?? false,
		maxDelayMs: options?.maxDelayMs,
	};
	return (src: Observable<T>) => {
		let retries = 0;
		return src.pipe(
			retry({
				delay: (error) => {
					retries += 1;
					const errorStatus = (error as { status?: number }).status ?? -1;
					if (
						retries >= parsedOptions.maxRetryAttempts ||
						!parsedOptions.includedStatusCodes.includes(errorStatus)
					) {
						// eslint-disable-next-line @typescript-eslint/no-unsafe-return
						return throwError(() => error);
					}
					const delayTime = calculateRetryDelay(
						retries,
						parsedOptions.scalingDuration,
						parsedOptions.backoff,
						parsedOptions.maxDelayMs,
						parsedOptions.jitter
					);
					return of(error).pipe(delay(delayTime));
				},
			})
		);
	};
};
